/**
 * BulkConfirmationModal
 *
 * Confirmation modal for bulk operations on selected rows.
 *
 * `scope` decides the wording, not just the number: under `'all'` the count
 * covers rows the user cannot see — hidden by a split, a column filter or the
 * visibility view, and on other pages — so the copy says so outright rather than
 * quoting a total the user cannot reconcile with the grid (SYD-2386).
 *
 * Duplicate never reaches the `'all'` wording: `ArBulkActionsMenu` disables it
 * under that scope, because the callable caps a duplicate at `AR_TABLE_PAGE_SIZE`.
 *
 * Delete is not here and cannot be: it applies immediately and the undo buttons
 * carry the way back (SYD-2447). The three that remain destroy nothing a press can
 * restore, so each still asks first.
 */

import { FC, memo } from 'react';
import { Modal, Typography } from 'antd';
import FlexCol from '../../atoms/FlexCol';
import type { BulkActionType } from './BulkActionType.types';

const { Text } = Typography;

type BulkConfirmationModalProps = {
  action: BulkActionType;
  selectedCount: number;
  scope: 'visible' | 'all';
  open: boolean;
  confirmLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const BulkConfirmationModal: FC<BulkConfirmationModalProps> = ({
  action,
  selectedCount,
  scope,
  open,
  confirmLoading = false,
  onConfirm,
  onCancel,
}) => {
  const rows = `${selectedCount.toLocaleString()} ${selectedCount === 1 ? 'row' : 'rows'}`;
  const rowNoun = selectedCount === 1 ? 'Row' : 'Rows';
  const isAllRows = scope === 'all';

  const titleByAction: Record<BulkActionType, string> = {
    lock: `Lock ${rowNoun}`,
    unlock: `Unlock ${rowNoun}`,
    duplicate: `Duplicate ${rowNoun}`,
  };

  const visibleMessageByAction: Record<BulkActionType, string> = {
    lock: `Are you sure you want to lock ${rows}?`,
    unlock: `Are you sure you want to unlock ${rows}?`,
    duplicate: `Are you sure you want to duplicate ${rows}?`,
  };

  const allRowsMessageByAction: Record<BulkActionType, string> = {
    lock: `Lock all ${rows} in this table?`,
    unlock: `Unlock all ${rows} in this table?`,
    duplicate: `Duplicate all ${rows} in this table?`,
  };

  return (
    <Modal
      aria-label="artable-bulk-confirmation-modal"
      title={titleByAction[action]}
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText="Confirm"
      cancelText="Cancel"
      confirmLoading={confirmLoading}
      destroyOnHidden
    >
      <FlexCol>
        <Text>{isAllRows ? allRowsMessageByAction[action] : visibleMessageByAction[action]}</Text>
        {isAllRows && (
          <Text type="secondary" aria-label="artable-bulk-confirmation-scope-note">
            This includes rows hidden by filters and rows on other pages.
          </Text>
        )}
      </FlexCol>
    </Modal>
  );
};

export default memo(BulkConfirmationModal);
